/**
 * StockReportScreen — current inventory valuation with low-stock alerts,
 * category breakdown, and PDF export.
 *
 * Shows every active, tracked product with stock on-hand, value at
 * purchase price, selling price, and a low-stock indicator.
 */

import { useCallback, useEffect, useState } from 'react';
import { Q } from '@nozbe/watermelondb';
import { AlertTriangle, Download, RefreshCw, Package } from 'lucide-react';
import { database } from '@/db';
import { Product } from '@/db/models/Inventory';
import { Category } from '@/db/models/Inventory';
import { useAppStore } from '@/store/useAppStore';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import { Button } from '@/components/ui/Button';
import { pdf, file } from '@/platform';
import { toCsv } from '@/utils/csv';
import { buildStockReport, filterStockRows, type StockReport, type StockFilter } from '@/reports/stock/stock';

// ─── Component ───────────────────────────────────────────────────────────────

export function StockReportScreen() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const [data, setData]           = useState<StockReport | null>(null);
  const [filter, setFilter]       = useState<StockFilter>('all');
  const [search, setSearch]       = useState('');
  const [loading, setLoading]     = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!activeStoreId) return;
    setLoading(true);
    try {
      const [products, categories] = await Promise.all([
        database.get<Product>('products').query(
          Q.where('store_id', activeStoreId),
          Q.where('is_deleted', false),
          Q.where('is_active', true),
          Q.where('track_inventory', true),
          Q.where('is_service', false),
        ).fetch(),
        database.get<Category>('categories').query(
          Q.where('is_deleted', false),
        ).fetch(),
      ]);

      setData(buildStockReport(products, categories));
    } finally {
      setLoading(false);
    }
  }, [activeStoreId]);

  useEffect(() => { void load(); }, [load]);

  const handleExport = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const html = buildStockHtml(data);
      await pdf.saveFromHtml({ html, defaultFileName: `StockReport_${new Date().toISOString().slice(0, 10)}.pdf` });
    } finally {
      setExporting(false);
    }
  };

  const visibleRows = filterStockRows(data?.rows ?? [], filter, search);

  const handleExportCsv = async () => {
    // Export exactly what the filter/search is showing, not the whole catalogue.
    if (visibleRows.length === 0) return;
    setExporting(true);
    try {
      const csv = toCsv(visibleRows, [
        { header: 'Product', value: (r) => r.name },
        { header: 'SKU', value: (r) => r.sku },
        { header: 'Category', value: (r) => r.category },
        { header: 'In stock', value: (r) => r.stock },
        { header: 'Unit', value: (r) => r.unit },
        { header: 'Purchase price', value: (r) => r.purchasePrice },
        { header: 'Selling price', value: (r) => r.sellingPrice },
        { header: 'Stock value (at cost)', value: (r) => r.stockValue },
        { header: 'Low-stock alert', value: (r) => r.lowStockAlert },
      ]);
      await file.saveText({
        content: csv,
        defaultFileName: `StockReport_${new Date().toISOString().slice(0, 10)}.csv`,
        filters: [{ name: 'CSV', extensions: ['csv'] }],
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {(['all', 'low', 'zero'] as StockFilter[]).map((f) => (
          <button key={f} type="button" onClick={() => setFilter(f)}
            className={cn(
              'rounded-md px-3 py-1.5 text-body transition-colors',
              filter === f ? 'bg-accent/10 text-accent-ink' : 'text-fg-secondary hover:bg-bg-subtle',
            )}>
            {f === 'all' ? 'All products' : f === 'low' ? 'Low stock' : 'Out of stock'}
          </button>
        ))}
        <input
          type="search" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search product or SKU…"
          className="ml-2 rounded-md border border-stroke-hairline/15 bg-bg-surface px-3 py-1.5 text-body text-fg-primary placeholder:text-fg-tertiary outline-none focus:border-accent/40"
        />
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
          </Button>
          <Button variant="secondary" onClick={() => void handleExportCsv()} disabled={!data || exporting || visibleRows.length === 0}>
            <Download size={14} className="mr-1.5" /> CSV
          </Button>
          <Button variant="secondary" onClick={() => void handleExport()} disabled={!data || exporting}>
            <Download size={14} className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-body text-fg-tertiary">Loading…</div>
      ) : !data ? null : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KPI label="Stock value (at cost)"  value={formatCurrency(data.totalValue)}     accent />
            <KPI label="Total products"          value={String(data.totalProducts)} />
            <KPI label="Low stock"               value={String(data.lowStockCount)}  warn={data.lowStockCount > 0} />
            <KPI label="Out of stock"            value={String(data.zeroStockCount)} warn={data.zeroStockCount > 0} />
          </div>

          {/* Table */}
          <div className="min-h-0 overflow-auto rounded-lg border border-stroke-hairline/10 bg-bg-surface">
            {visibleRows.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Package size={32} className="text-fg-tertiary" />
                <div className="mt-2 text-body text-fg-tertiary">No products match this filter.</div>
              </div>
            ) : (
              <table className="w-full text-body">
                <thead className="sticky top-0 bg-bg-surface text-label uppercase text-fg-tertiary">
                  <tr className="border-b border-stroke-hairline/10">
                    <th className="px-4 py-2 text-left font-normal">Product</th>
                    <th className="px-4 py-2 text-left font-normal">Category</th>
                    <th className="px-4 py-2 text-right font-normal">In stock</th>
                    <th className="px-4 py-2 text-right font-normal">Purchase ₹</th>
                    <th className="px-4 py-2 text-right font-normal">Selling ₹</th>
                    <th className="px-4 py-2 text-right font-normal">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((r) => (
                    <tr key={r.id} className={cn('border-b border-stroke-hairline/10', r.stock <= 0 && 'opacity-50')}>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1.5">
                          {r.isLow && r.stock > 0 && <AlertTriangle size={12} className="shrink-0 text-warning-ink" />}
                          {r.stock <= 0 && <AlertTriangle size={12} className="shrink-0 text-danger-ink" />}
                          <span className="text-fg-primary">{r.name}</span>
                        </div>
                        {r.sku && <div className="font-mono text-label text-fg-tertiary">{r.sku}</div>}
                      </td>
                      <td className="px-4 py-2.5 text-fg-secondary">{r.category}</td>
                      <td className={cn(
                        'px-4 py-2.5 text-right tabular-nums',
                        r.stock <= 0 ? 'text-danger-ink font-medium' : r.isLow ? 'text-warning-ink font-medium' : 'text-fg-primary',
                      )}>
                        {r.stock} {r.unit}
                      </td>
                      <td className="px-4 py-2.5 text-right text-fg-secondary tabular-nums">{formatCurrency(r.purchasePrice)}</td>
                      <td className="px-4 py-2.5 text-right text-fg-secondary tabular-nums">{formatCurrency(r.sellingPrice)}</td>
                      <td className="px-4 py-2.5 text-right text-fg-primary tabular-nums">{formatCurrency(r.stockValue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Shared UI ────────────────────────────────────────────────────────────────

function KPI({ label, value, accent, warn }: { label: string; value: string; accent?: boolean; warn?: boolean }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="text-label uppercase text-fg-tertiary">{label}</div>
      <div className={cn(
        'mt-1 text-heading tabular-nums',
        accent ? 'text-accent-ink' : warn ? 'text-warning-ink' : 'text-fg-primary',
      )}>
        {value}
      </div>
    </div>
  );
}

// ─── PDF ─────────────────────────────────────────────────────────────────────

function buildStockHtml(data: StockReport): string {
  const fmt = (n: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);
  // Catalog text is merchant-entered and reaches this template unfiltered, so it
  // must be escaped: raw markup here corrupts the report and can pull remote
  // resources (e.g. an <img> in a product name) when the PDF/print page renders.
  const rows = data.rows.map((r) => `<tr>
    <td>${escapeHtml(r.name)}${r.sku ? `<br/><small style="color:#888">${escapeHtml(r.sku)}</small>` : ''}</td>
    <td>${escapeHtml(r.category)}</td>
    <td align="right" ${r.stock <= 0 ? 'style="color:red"' : r.isLow ? 'style="color:orange"' : ''}>${r.stock} ${escapeHtml(r.unit)}</td>
    <td align="right">${fmt(r.purchasePrice)}</td>
    <td align="right">${fmt(r.sellingPrice)}</td>
    <td align="right">${fmt(r.stockValue)}</td>
  </tr>`).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>body{font-family:sans-serif;margin:32px;color:#222}h1{font-size:22px}h2{font-size:14px;margin-top:24px;text-transform:uppercase;color:#666;border-bottom:1px solid #eee;padding-bottom:4px}
table{width:100%;border-collapse:collapse;font-size:12px}th,td{padding:5px 8px;border-bottom:1px solid #eee}th{text-align:left;color:#666;font-weight:500}
.kpi-grid{display:flex;gap:24px;margin:12px 0}.kpi{min-width:120px}.kpi-label{font-size:11px;text-transform:uppercase;color:#888}.kpi-value{font-size:18px;font-weight:700}</style></head>
<body>
<h1>Stock Report</h1>
<p style="color:#888">${new Date().toLocaleDateString('en-IN')}</p>
<div class="kpi-grid">
  <div class="kpi"><div class="kpi-label">Total Value</div><div class="kpi-value" style="color:#4F46E5">${fmt(data.totalValue)}</div></div>
  <div class="kpi"><div class="kpi-label">Products</div><div class="kpi-value">${data.totalProducts}</div></div>
  <div class="kpi"><div class="kpi-label">Low Stock</div><div class="kpi-value" style="color:orange">${data.lowStockCount}</div></div>
  <div class="kpi"><div class="kpi-label">Zero Stock</div><div class="kpi-value" style="color:red">${data.zeroStockCount}</div></div>
</div>
<table>
  <tr><th>Product</th><th>Category</th><th align="right">In Stock</th><th align="right">Purchase</th><th align="right">Selling</th><th align="right">Value</th></tr>
  ${rows}
</table>
<p style="margin-top:40px;font-size:11px;color:#aaa">Generated by BigxBook on ${new Date().toLocaleString('en-IN')}</p>
</body></html>`;
}

function escapeHtml(s: string): string {
  return String(s ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);
}
